import * as React from "react";
import {
  CreatableSelect,
  Select as ReactSelect,
  SelectInstance
} from "chakra-react-select";
import { get, uniqBy } from "lodash-es";
import { generateId, useField } from "duck";
import InputLayout from "../input-layout";
import * as duck from "./duck";

const Select = <
  TValue extends string | number = string,
  TOption = duck.InternalOption<TValue>,
  IsMulti extends boolean = false
>({
  name,
  value,
  onChange,
  onBlur,
  isMulti,
  isDisabled = false,
  isClearable = true,
  options: defaultOptions,
  label,
  labelHidden,
  controlShouldRenderValue = true,
  placeholder,
  isSearchable = true,
  inputLayoutProps,
  noInputWrap = false,
  chakraStyles,
  menuPlacement = "auto",
  creatable,
  getNewOptionLabel,
  size = "md",
  isValidNewOption,
  innerRef,
  isLoading,
  optionValue = "value",
  optionLabel = "label",
  isOptionDisabled,
  components
}: duck.SelectProps<TValue, TOption, IsMulti>) => {
  const labelId = React.useMemo(() => name || generateId(), [name]);
  const [createdOptions, setCreatedOptions] = React.useState<
    duck.InternalOption<TValue>[]
  >([]);

  const fieldProps = useField(name);

  const options = [...createdOptions, ...defaultOptions];
  const valueOption = duck.resolveValue(
    value,
    fieldProps.value,
    options,
    optionValue
  );

  React.useImperativeHandle(
    innerRef,
    () => ({
      resetCreatedOptions() {
        setCreatedOptions([]);
      }
    }),
    []
  );

  return (
    <InputLayout
      id={labelId}
      label={label}
      labelHidden={labelHidden}
      errorMessage={fieldProps.error}
      input={React.createElement<any>(
        creatable ? CreatableSelect : ReactSelect,
        {
          name: labelId,
          ref: (ref: SelectInstance | null) => {
            if (fieldProps.ref) {
              fieldProps.ref(ref?.inputRef || null);
            }
          },
          selectedOptionStyle: "check",
          useBasicStyles: true,
          isMulti,
          placeholder,
          isLoading,
          isDisabled,
          hideSelectedOptions: false,
          value: valueOption || null,
          onBlur: () => {
            if (onBlur) {
              onBlur();
            } else if (fieldProps.onBlur) {
              fieldProps.onBlur();
            }
          },
          onChange: (changeOptions: any) => {
            if (creatable) {
              const selectedNewOptions = duck.getCreatedOptions(changeOptions);
              setCreatedOptions(prev =>
                uniqBy([...prev, ...selectedNewOptions], optionValue)
              );
            }

            const [ids, opts] = duck.optionsToValue(changeOptions, optionValue);
            if (onChange) {
              onChange(ids, opts);
            } else if (fieldProps.onChange) {
              fieldProps.onChange(ids, opts);
            }
          },
          isSearchable,
          menuPlacement,
          isOptionDisabled,
          isClearable: controlShouldRenderValue === false ? false : isClearable,
          controlShouldRenderValue,
          options,
          getOptionLabel: (opt: any) => get(opt, optionLabel),
          getOptionValue: (opt: any) => get(opt, optionValue),

          getNewOptionData: getNewOptionLabel
            ? (val: string) => ({
                value: val,
                label: getNewOptionLabel(val)
              })
            : undefined,
          isValidNewOption,
          size,
          chakraStyles: {
            ...duck.BASE_CHAKRA_STYLES,
            ...(noInputWrap && {
              singleValue: (provided: any) => ({
                ...provided,
                overflow: "hidden",
                whiteSpace: "nowrap"
              })
            }),
            ...chakraStyles
          },
          components
        }
      )}
      {...inputLayoutProps}
    />
  );
};

export default Select;
